import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import { Router } from "@angular/router";
import { HeaderComponent } from "../components/header/header.component";
import { SidebarComponent } from "../components/sidebar/sidebar.component";
import { SubheaderComponent } from "../components/subheader/subheader.component";
import { LoadingComponent } from "../shared/components/loading/loading.component";
import { CustomValidators } from "../shared/validators/custom-validators";
import { MockApiService } from "../shared/services/mock-api.service";
import {
  FormStateService,
  FormState,
} from "../shared/services/form-state.service";
import { ToastService } from "../shared/services/toast.service";
import { UserRegistration } from "../shared/models/form-models";
import { Observable } from "rxjs";
import { InputMaskModule } from "primeng/inputmask";
import { InputNumberModule } from "primeng/inputnumber";
import { DropdownModule } from "primeng/dropdown";
import { LoanDetails } from "../shared/models/form-models";
import { GeneralModalComponent } from "../shared/components/general-modal/general-modal.component";

@Component({
  selector: "app-registration-form",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SidebarComponent,
    HeaderComponent,
    SubheaderComponent,
    LoadingComponent,
    InputMaskModule,
    InputNumberModule,
    DropdownModule,
    GeneralModalComponent,
  ],
  templateUrl: "./registration-form.component.html",
  styleUrl: "./registration-form.component.css",
})
export class RegistrationFormComponent implements OnInit {
  registrationForm!: FormGroup;
  formState$: Observable<FormState>;
  // En tu componente .ts
  requestNumber = "ST0001"; // o el valor que obtengas dinámicamente
  showRequestNumberModal = false;

  
  termsOptions = [
    { label: "Seleccionar...", value: null },
    { label: "16 semanas", value: "16" },
    { label: "20 semanas", value: "20" },
    { label: "24 semanas", value: "24" },
    { label: "28 semanas", value: "28" },
  ];

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private mockApiService: MockApiService,
    private formStateService: FormStateService,
    private toastService: ToastService
  ) {
    this.formState$ = this.formStateService.formState$;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  // Validador personalizado para el monto
  static amountValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return { required: true };
      }

      // Remover formato de moneda para validar
      const numericValue = parseFloat(
        control.value.toString().replace(/[^\d]/g, "")
      );

      if (isNaN(numericValue)) {
        return { invalidAmount: true };
      }

      if (numericValue < 5000) {
        return { minAmount: true };
      }

      if (numericValue > 60000) {
        return { maxAmount: true };
      }

      if (numericValue % 100 !== 0) {
        return { notMultipleOf100: true };
      }

      return null;
    };
  }

  // Método para manejar la entrada del usuario
  // Agregar propiedades para los detalles del préstamo
  loanDetails: LoanDetails | null = null;
  loadingLoanDetails: boolean = false;

  onAmountInput(event: any): void {
    let value = event.target.value;

    // Remover todo excepto números
    value = value.replace(/[^\d]/g, "");

    // Limitar a 6 dígitos (máximo 60000)
    if (value.length > 5) {
      value = value.substring(0, 5);
    }

    // Aplicar formato de moneda
    if (value) {
      const numericValue = parseInt(value);
      const formattedValue = this.formatNumberToCurrency(numericValue);

      // Actualizar el valor del control sin disparar el evento
      this.registrationForm
        .get("requestedAmount")
        ?.setValue(formattedValue, { emitEvent: false });

      // Actualizar el valor visual del input
      event.target.value = formattedValue;

      // Obtener detalles del préstamo si el monto es válido
      if (
        numericValue >= 5000 &&
        numericValue <= 60000 &&
        numericValue % 100 === 0
      ) {
        this.getLoanDetails(numericValue);
      } else {
        this.loanDetails = null;
      }
    } else {
      this.loanDetails = null;
    }
  }

  // Método para formatear número a moneda
  formatNumberToCurrency(value: number): string {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  // Método para formatear al perder el foco
  // Cambiar el método formatCurrency para que acepte un parámetro
  formatCurrency(value?: number): string {
    if (value !== undefined) {
      return this.formatNumberToCurrency(value);
    }

    // Comportamiento original para formatear el campo del formulario
    const control = this.registrationForm.get("requestedAmount");
    if (control?.value) {
      const numericValue = parseFloat(
        control.value.toString().replace(/[^\d]/g, "")
      );
      if (!isNaN(numericValue)) {
        const formattedValue = this.formatNumberToCurrency(numericValue);
        control.setValue(formattedValue);

        // Obtener detalles del préstamo si el monto es válido
        if (
          numericValue >= 5000 &&
          numericValue <= 60000 &&
          numericValue % 100 === 0
        ) {
          this.getLoanDetails(numericValue);
        } else {
          this.loanDetails = null;
        }
      }
    }
    return "";
  }

  // Inicializar formulario
  private initializeForm(): void {
    this.registrationForm = this.fb.group(
      {
        phoneNumber: ["", [Validators.required, CustomValidators.mexicanPhone]],
        confirmPhoneNumber: ["", [Validators.required]],
        email: ["", [Validators.required, CustomValidators.email]],
        term: [{ value: "16", disabled: true }, [Validators.required]],
        requestedAmount: ["", [RegistrationFormComponent.amountValidator()]],
      },
      {
        validators: this.phoneNumbersMatchValidator,
      }
    );
  }

  // Validador personalizado para verificar que los números coincidan
  phoneNumbersMatchValidator(
    control: AbstractControl
  ): { [key: string]: any } | null {
    const phoneNumber = control.get("phoneNumber");
    const confirmPhoneNumber = control.get("confirmPhoneNumber");

    if (!phoneNumber || !confirmPhoneNumber) {
      return null;
    }

    if (phoneNumber.value !== confirmPhoneNumber.value) {
      confirmPhoneNumber.setErrors({ phoneNumberMismatch: true });
      return { phoneNumberMismatch: true };
    } else {
      // Si coinciden, remover el error de mismatch pero mantener otros errores
      if (confirmPhoneNumber.errors) {
        delete confirmPhoneNumber.errors["phoneNumberMismatch"];
        if (Object.keys(confirmPhoneNumber.errors).length === 0) {
          confirmPhoneNumber.setErrors(null);
        }
      }
    }

    return null;
  }

  // Getters para fácil acceso a los controles
  get phoneNumber() {
    return this.registrationForm.get("phoneNumber");
  }
  get confirmPhoneNumber() {
    return this.registrationForm.get("confirmPhoneNumber");
  }
  get email() {
    return this.registrationForm.get("email");
  }
  get term() {
    return this.registrationForm.get("term");
  }
  get requestedAmount() {
    return this.registrationForm.get("requestedAmount");
  }

  // Método unificado para obtener mensajes de error
  getErrorMessage(controlName: string): string {
    const control = this.registrationForm.get(controlName);
    if (!control || !control.errors || !control.touched) return "";

    const errors = control.errors;

    // Errores específicos para requestedAmount
    if (controlName === "requestedAmount") {
      if (errors["required"]) return "El monto es requerido";
      if (errors["invalidAmount"]) return "Ingresa un monto válido";
      if (errors["minAmount"]) return "El monto mínimo es $5,000";
      if (errors["maxAmount"]) return "El monto máximo es $60,000";
      if (errors["notMultipleOf100"])
        return "El monto debe ser múltiplo de $100";
    }

    // Errores generales
    if (errors["required"]) return "Este campo es obligatorio";
    if (errors["maxlength"])
      return `Máximo ${errors["maxlength"].requiredLength} dígitos`;
    if (errors["invalidPhone"])
      return "Ingresa un número de teléfono válido (10 dígitos)";
    if (errors["phoneNumberMismatch"])
      return "Los números de teléfono no coinciden";
    if (errors["invalidEmail"]) return "Ingresa un correo electrónico válido";
    if (errors["minlength"])
      return `Mínimo ${errors["minlength"].requiredLength} caracteres`;

    return "Campo inválido";
  }

  // Método para verificar si un campo tiene errores
  hasError(controlName: string): boolean {
    const control = this.registrationForm.get(controlName);
    return !!(control && control.errors && control.touched);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.registrationForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  // Método para marcar todos los campos como tocados
  private markFormGroupTouched(): void {
    Object.keys(this.registrationForm.controls).forEach((key) => {
      const control = this.registrationForm.get(key);
      if (control) {
        control.markAsTouched();

        // Si es un FormGroup anidado, marcar sus controles también
        if (control instanceof FormGroup) {
          this.markFormGroupTouched();
        }
      }
    });
  }

  goBack() {
      this.showCancelModal = true;
    //this.router.navigate(['/welcome']);
  }

  onSubmit(): void {
    if (this.registrationForm.valid) {
      this.formStateService.setLoading(true);

      const formData: UserRegistration = this.registrationForm.value;

      this.mockApiService.registerUser(formData).subscribe({
        next: (response) => {
          this.formStateService.setSuccess(response.message);
          this.toastService.showSuccess("¡Registro exitoso! Redirigiendo...");

          /*setTimeout(() => {
            this.router.navigate(["/otp"]);
          }, 2000);*/
          this.showRequestNumberModal = true;
        },
        error: (error) => {
          this.formStateService.setError(
            error.message || "Error al registrar usuario"
          );
          this.toastService.showError(
            error.message || "Error al registrar usuario"
          );
        },
      });
    } else {
      this.markFormGroupTouched();
      this.toastService.showWarning(
        "Por favor, completa todos los campos requeridos"
      );
    }
  }

  // Nuevo método para obtener detalles del préstamo
  private getLoanDetails(amount: number): void {
    this.loadingLoanDetails = true;
    this.loanDetails = null;

    this.mockApiService.getLoanDetails(amount, 16).subscribe({
      next: (response) => {
        this.loanDetails = response.data || null; // Agregar || null para manejar undefined
        this.loadingLoanDetails = false;
      },
      error: (error) => {
        this.loadingLoanDetails = false;
        this.toastService.showError("Error al obtener detalles del préstamo");
      },
    });
  }

  showCancelModal: boolean = false;
  showInfoModal: boolean = false;
  showCustomModal: boolean = false;

  openCancelModal() {
    this.showCancelModal = true;
  }

  onConfirmCancel() {
    this.router.navigate(["/"]);
  }

  onCancelModal() {
    this.showCancelModal = false;
  }

  // Métodos para otros modales
  openInfoModal() {
    this.showInfoModal = true;
  }

  closeInfoModal() {
    this.showInfoModal = false;
  }



  async copyRequestNumber() {
    try {
      await navigator.clipboard.writeText(this.requestNumber);
      // Opcional: feedback al usuario
      // this.toast.success('Número copiado');
    } catch {
      // Fallback si clipboard falla
      const ta = document.createElement("textarea");
      ta.value = this.requestNumber;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      // this.toast.success('Número copiado');
    }
  }

  onAcceptRequestNumber() {
    this.showRequestNumberModal = false;
    this.router.navigate(["/otp"]);
    // Acción adicional si aplica…
  }

  onCloseRequestNumberModal() {
    this.showRequestNumberModal = false;
  }
}
